import { useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, IconButton, Drawer, CircularProgress } from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  fetchRoomsCalendar, updateRatePrice, createRatePrice,
  updateAvailability, updateStatus
} from "./inventoryService"; // adapter le chemin si besoin
import InventoryGrid, {
  LABEL_COL, COLORS, unitOf, isPeriodPlan, normalizeStayType
} from "./InventoryGrid";
import BulkEditDrawer from "./BulkEditDrawer";
import { eurToKmf, formatKmf } from "./utils/currency"; // adapter le chemin si besoin

const API = "https://api.komorotel.com/api";

const DAYS_PER_PAGE = 14;

/* ═══════════════════════════════════════════════════════════
   DATES — toujours en heure LOCALE.

   toISOString() convertit d'abord en UTC : à Paris l'été, le 1er septembre
   à minuit devient le 31 août à 22h, et toute la grille se décale d'un jour.
   On formate donc à la main.
   ═══════════════════════════════════════════════════════════ */
const pad2 = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// Midi évite tout effet de bord de fuseau à la relecture d'une chaîne de date.
const parseDay = (s) => new Date(`${s}T12:00:00`);

const Calendar = ({ setActivePage, setNeedRatePlanMsg }) => {
  const { hotelId } = useParams();

  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // La grille s'ouvre sur AUJOURD'HUI, pas sur le 1er du mois : un hébergeur
  // qui consulte son calendrier le 18 n'a rien à faire des deux semaines
  // écoulées. L'état initial le fait dès le premier rendu, sans effet
  // secondaire qui provoquerait un saut visible.
  const [startIndex, setStartIndex] = useState(() => new Date().getDate() - 1);

  const [selectedDays, setSelectedDays] = useState({ tarif: [], inventory: [], status: [] });
  const [editType, setEditType] = useState(null);
  const [editModeRoom, setEditModeRoom] = useState(null);
  const [editRatePlan, setEditRatePlan] = useState(null);
  const [newValue, setNewValue] = useState("");

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);

  const [bulkDrawer, setBulkDrawer] = useState({ open: false, type: null, roomId: null, ratePlanId: null });
  const [bulkValue, setBulkValue] = useState("");
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  // ── Jours du mois ──
  const daysOfMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const count = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: count }, (_, i) => localISO(new Date(year, month, i + 1)));
  }, [currentDate]);

  // Le nombre de colonnes suit les jours RÉELLEMENT affichés : la dernière
  // page d'un mois en contient souvent moins de 14, et garder 14 colonnes
  // décalait toutes les lignes.
  const visibleDays = daysOfMonth.slice(startIndex, startIndex + DAYS_PER_PAGE);
  const gridTemplate = `${LABEL_COL}px repeat(${visibleDays.length}, minmax(0, 1fr))`;

  // ── Chargement ──
  const loadCalendar = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      // La borne de fin est EXCLUSIVE côté API : c'est la convention suivie
      // partout ailleurs ici, où l'on envoie toujours le dernier jour + 1.
      // En envoyant le dernier jour du mois tel quel, il n'était jamais
      // renvoyé : sa cellule tombait sur les valeurs par défaut, donc sans
      // prix, stock à 0 et statut fermé.
      const from = localISO(new Date(year, month, 1));
      const to   = localISO(new Date(year, month + 1, 1));

      const [calendarData, ratePlansRes] = await Promise.all([
        fetchRoomsCalendar(hotelId, from, to),
        axios.get(`${API}/rateplans/hotel/${hotelId}`, { withCredentials: true }),
      ]);
      setRooms(calendarData);
      setRatePlans(ratePlansRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCalendar(); }, [hotelId, currentDate]);

  // Une sélection en cours ne survit pas au relâchement de la souris hors grille
  useEffect(() => {
    const stop = () => { setIsSelecting(false); setSelectionStart(null); };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, []);

  const resetSelection = () => {
    setSelectedDays({ tarif: [], inventory: [], status: [] });
    setEditType(null);
    setEditModeRoom(null);
    setEditRatePlan(null);
    setNewValue("");
  };

  const changeMonth = (delta) => {
    const next = new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1);
    const now = new Date();
    const isCurrentMonth =
      next.getFullYear() === now.getFullYear() && next.getMonth() === now.getMonth();

    setCurrentDate(next);
    setStartIndex(isCurrentMonth ? now.getDate() - 1 : 0);
    resetSelection();
  };

  const goToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setStartIndex(now.getDate() - 1);
    resetSelection();
  };

  const openBulk = ({ type, roomId, ratePlanId = null }) => {
    setBulkDrawer({ open: true, type, roomId, ratePlanId });
    setBulkValue("");
  };

  // ── Enregistrement d'une sélection ──
  const handleSave = async () => {
    if (!newValue || !editType || !editModeRoom) return;
    const indices = selectedDays[editType];
    if (!indices?.length) return;

    const startDate = daysOfMonth[startIndex + Math.min(...indices)];
    const endRaw = daysOfMonth[startIndex + Math.max(...indices)];
    const endDate = localISO(new Date(parseDay(endRaw).getTime() + 86400000));

    try {
      if (editType === "tarif") {
        const room = rooms.find(r => r.roomId === editModeRoom);
        const firstDay = room?.days[startIndex + Math.min(...indices)];
        const existing = firstDay?.rates?.find(r =>
          (r.ratePlanId?._id || r.ratePlanId)?.toString() === editRatePlan
        );
        if (existing?.ratePriceId) await updateRatePrice(existing.ratePriceId, Number(newValue));
        else await createRatePrice(editModeRoom, editRatePlan, Number(newValue), startDate, endDate);
      }
      if (editType === "inventory") await updateAvailability(editModeRoom, startDate, endDate, Number(newValue));
      if (editType === "status")    await updateStatus(editModeRoom, startDate, endDate, newValue);

      await loadCalendar();
      resetSelection();
    } catch (err) {
      console.error(err);
    }
  };

  // ── Application sur tout le mois (drawer par chambre) ──
  const handleApplyBulk = async () => {
    if (!bulkValue || !bulkDrawer.roomId) return;
    const startDate = daysOfMonth[0];
    const endDate = localISO(new Date(parseDay(daysOfMonth[daysOfMonth.length - 1]).getTime() + 86400000));

    try {
      if (bulkDrawer.type === "price") {
        if (!bulkDrawer.ratePlanId) return;
        await createRatePrice(bulkDrawer.roomId, bulkDrawer.ratePlanId, Number(bulkValue), startDate, endDate);
      }
      if (bulkDrawer.type === "inventory") {
        await updateAvailability(bulkDrawer.roomId, startDate, endDate, Number(bulkValue));
      }
      await loadCalendar();
      setBulkDrawer({ open: false, type: null, roomId: null, ratePlanId: null });
      setBulkValue("");
    } catch (err) {
      console.error(err);
    }
  };

  // ── Modification groupée multi-chambres ──
  const handleBulkEditApply = async ({ roomIds, startDate, endDate, type, value, ratePlanId }) => {
    const start = localISO(startDate);
    const end = localISO(new Date(endDate.getTime() + 86400000));
    for (const roomId of roomIds) {
      if (type === "price" && ratePlanId) await createRatePrice(roomId, ratePlanId, value, start, end);
      if (type === "inventory") await updateAvailability(roomId, start, end, value);
      if (type === "status")    await updateStatus(roomId, start, end, value);
    }
    await loadCalendar();
  };

  const monthLabel = currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const selectedCount = editType ? selectedDays[editType]?.length || 0 : 0;

  const todayISO = localISO(new Date());
  const isCurrentMonth =
    currentDate.getFullYear() === new Date().getFullYear() &&
    currentDate.getMonth() === new Date().getMonth();

  // Tarif visé par la sélection, pour rappeler son unité dans la barre du bas
  const selectedPlan = editType === "tarif" && editRatePlan
    ? ratePlans.find(p => p._id?.toString() === editRatePlan?.toString())
    : null;
  const selectedUnit = selectedPlan ? unitOf(selectedPlan.stayType) : unitOf("short_stay");
  const selectedIsPeriod = selectedPlan ? isPeriodPlan(selectedPlan.stayType) : false;

  // Tarif visé par le drawer
  const drawerPlan = bulkDrawer.ratePlanId
    ? ratePlans.find(p => p._id.toString() === bulkDrawer.ratePlanId?.toString())
    : null;
  const drawerUnit = drawerPlan ? unitOf(drawerPlan.stayType) : unitOf("short_stay");
  const drawerIsPeriod = drawerPlan ? isPeriodPlan(drawerPlan.stayType) : false;

  const drawerRoomPlans = bulkDrawer.roomId
    ? ratePlans.filter(p =>
        (p.roomIds || []).map(id => id?.toString?.() ?? id).includes(bulkDrawer.roomId?.toString())
      )
    : [];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh" }}>

      {/* ══ EN-TÊTE ══ */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mb: 4,
      }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
            Calendrier
          </Typography>
          <Typography sx={{ fontSize: 14, color: COLORS.muted, mt: 0.5 }}>
            Gérez vos prix, stocks et disponibilités
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={() => setBulkEditOpen(true)}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 600,
              borderColor: COLORS.border, color: COLORS.text,
              "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#FFFFFF" },
            }}
          >
            Modification groupée
          </Button>

          {/* Le mois est un sélecteur compact plutôt que trois éléments
              dispersés : une seule zone à viser. */}
          <Box sx={{
            display: "flex", alignItems: "center",
            backgroundColor: "#FFFFFF",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            overflow: "hidden",
          }}>
            <IconButton onClick={() => changeMonth(-1)} size="small" sx={{ borderRadius: 0, px: 1 }}>
              <ChevronLeft size={18} />
            </IconButton>
            <Typography sx={{
              minWidth: 150, textAlign: "center", fontSize: 14, fontWeight: 600,
              textTransform: "capitalize", color: COLORS.text,
            }}>
              {monthLabel}
            </Typography>
            <IconButton onClick={() => changeMonth(1)} size="small" sx={{ borderRadius: 0, px: 1 }}>
              <ChevronRight size={18} />
            </IconButton>
          </Box>

          {!isCurrentMonth && (
            <Button
              onClick={goToday}
              startIcon={<CalendarDays size={15} />}
              sx={{ textTransform: "none", fontWeight: 600, color: "var(--k-green)" }}
            >
              Aujourd'hui
            </Button>
          )}
        </Box>
      </Box>

      {/* ══ CARTE UNIQUE ══
          En-tête des jours et lignes des chambres partagent la MÊME grille.
          Deux grilles séparées ne peuvent pas rester alignées : c'est ce qui
          provoquait les décalages et les colonnes coupées. */}
      <Box sx={{
        backgroundColor: "#FFFFFF",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 3,
        overflow: "hidden",
      }}>

        {/* Pagination des jours */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1.75,
          borderBottom: `1px solid ${COLORS.border}`,
        }}>
          <Button
            size="small"
            disabled={startIndex === 0}
            onClick={() => setStartIndex(Math.max(0, startIndex - DAYS_PER_PAGE))}
            sx={{ textTransform: "none", fontWeight: 600, color: COLORS.text, minWidth: 0 }}
          >
            ← Précédents
          </Button>

          <Typography sx={{ fontSize: 13, color: COLORS.muted }}>
            {visibleDays.length > 0 && (
              <>
                {parseDay(visibleDays[0]).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                {" – "}
                {parseDay(visibleDays[visibleDays.length - 1]).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
              </>
            )}
          </Typography>

          <Button
            size="small"
            disabled={startIndex + DAYS_PER_PAGE >= daysOfMonth.length}
            onClick={() => setStartIndex(startIndex + DAYS_PER_PAGE)}
            sx={{ textTransform: "none", fontWeight: 600, color: COLORS.text, minWidth: 0 }}
          >
            Suivants →
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={26} sx={{ color: "var(--k-green)" }} />
          </Box>
        ) : rooms.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 14 }}>
              Aucune chambre configurée
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: gridTemplate, userSelect: "none" }}>

            {/* ── En-tête des jours ── */}
            <Box sx={{
              height: 56,
              backgroundColor: COLORS.label,
              borderRight: `1px solid ${COLORS.border}`,
            }} />

            {visibleDays.map((dateStr, i) => {
              const date = parseDay(dateStr);
              const isToday = dateStr === todayISO;
              const isWeekend = [0, 6].includes(date.getDay());
              return (
                <Box key={i} sx={{
                  height: 56,
                  borderLeft: `1px solid ${COLORS.borderSoft}`,
                  backgroundColor: isToday
                    ? "var(--k-green-soft)"
                    : isWeekend ? COLORS.weekend : COLORS.label,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  minWidth: 0,
                }}>
                  <Typography sx={{
                    fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em",
                    color: isToday ? "var(--k-green)" : COLORS.muted, fontWeight: 600,
                  }}>
                    {date.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "")}
                  </Typography>
                  <Typography sx={{
                    fontSize: 15,
                    fontWeight: isToday ? 800 : 600,
                    color: isToday ? "var(--k-green)" : COLORS.text,
                    lineHeight: 1.2,
                  }}>
                    {date.getDate()}
                  </Typography>
                </Box>
              );
            })}

            {/* ── Lignes des chambres ── */}
            {rooms.map((room, i) => (
              <InventoryGrid
                key={room.roomId}
                room={room}
                visibleDays={visibleDays}
                startIndex={startIndex}
                selectedDays={selectedDays}
                setSelectedDays={setSelectedDays}
                editType={editType}
                setEditType={setEditType}
                editModeRoom={editModeRoom}
                setEditModeRoom={setEditModeRoom}
                isSelecting={isSelecting}
                setIsSelecting={setIsSelecting}
                selectionStart={selectionStart}
                setSelectionStart={setSelectionStart}
                editRatePlan={editRatePlan}
                setEditRatePlan={setEditRatePlan}
                openBulk={openBulk}
                goToRatePlans={setActivePage ? () => setActivePage("tarifs") : undefined}
                allRatePlans={ratePlans}
                isLastRoom={i === rooms.length - 1}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ══ LÉGENDE ══ */}
      {!loading && rooms.length > 0 && (
        <Box sx={{ display: "flex", gap: 2.5, mt: 2, flexWrap: "wrap" }}>
          {[
            { c: COLORS.missing, label: "Prix à définir" },
            { c: COLORS.closed,  label: "Stock épuisé" },
            { c: "#ECFDF3",      label: "Ouvert à la vente" },
          ].map((l, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: .75, backgroundColor: l.c, border: `1px solid ${COLORS.border}` }} />
              <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{l.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ══ BARRE D'ACTION ══ */}
      {selectedCount > 0 && (
        <Box sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#FFFFFF",
          borderRadius: 3,
          boxShadow: "0 12px 40px rgba(15,23,42,.16)",
          border: `1px solid ${COLORS.border}`,
          px: 2.5, py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          zIndex: 100,
          maxWidth: "calc(100vw - 32px)",
          flexWrap: "wrap",
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
              {selectedCount} jour{selectedCount > 1 ? "s" : ""} sélectionné{selectedCount > 1 ? "s" : ""}
            </Typography>
            {editType === "tarif" && selectedPlan && (
              <Typography sx={{
                fontSize: 11,
                color: selectedIsPeriod ? "var(--k-green)" : COLORS.muted,
                fontWeight: selectedIsPeriod ? 600 : 400,
              }}>
                {selectedPlan.name} · prix / {selectedUnit.label}
              </Typography>
            )}
          </Box>

          {/* Le statut est un choix, pas une saisie : taper "opened" ou
              "closed" exposait les valeurs techniques et laissait passer
              les fautes de frappe. */}
          {editType === "status" ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              {[
                { value: "opened", label: "Ouvert", bg: "#ECFDF3", color: "#15803D" },
                { value: "closed", label: "Fermé",  bg: "#F5F7FA", color: "#475569" },
              ].map(opt => {
                const active = newValue === opt.value;
                return (
                  <Button
                    key={opt.value}
                    onClick={() => setNewValue(opt.value)}
                    sx={{
                      minWidth: 92, borderRadius: 2, textTransform: "none", fontWeight: 600,
                      border: active ? `1.5px solid ${COLORS.selected}` : `1px solid ${COLORS.border}`,
                      backgroundColor: active ? opt.bg : "transparent",
                      color: active ? opt.color : COLORS.muted,
                      "&:hover": { backgroundColor: opt.bg },
                    }}
                  >
                    {opt.label}
                  </Button>
                );
              })}
            </Box>
          ) : (
            <Box>
              <input
                type="number"
                autoFocus
                placeholder={editType === "tarif"
                  ? `Prix (€ / ${selectedUnit.label})`
                  : "Nombre de chambres"}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newValue) handleSave(); }}
                style={{
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 14,
                  outline: "none",
                  width: 190,
                }}
              />
              {editType === "tarif" && newValue > 0 && (
                <Typography sx={{ fontSize: 11, color: COLORS.muted, mt: 0.5, pl: 0.5 }}>
                  {formatKmf(eurToKmf(newValue))} KMF
                  {selectedIsPeriod && ` · soit ≈ ${Math.round(Number(newValue) / selectedUnit.nights)} €/nuit`}
                </Typography>
              )}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!newValue}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700,
              backgroundColor: "var(--k-green)",
              "&:hover": { backgroundColor: "var(--k-green-hover)" },
            }}
          >
            Appliquer
          </Button>

          <Button onClick={resetSelection} sx={{ textTransform: "none", color: COLORS.muted }}>
            Annuler
          </Button>
        </Box>
      )}

      {/* ══ DRAWER — application sur tout le mois ══ */}
      <Drawer
        anchor="right"
        open={bulkDrawer.open}
        onClose={() => setBulkDrawer({ open: false, type: null, roomId: null, ratePlanId: null })}
        PaperProps={{ sx: { width: 380, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

          <Box sx={{ p: 3, borderBottom: `1px solid ${COLORS.border}` }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: COLORS.text }}>
              {bulkDrawer.type === "price"
                ? (drawerPlan ? drawerPlan.name : "Configurer un prix")
                : "Modifier le stock"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 0.25 }}>
              Appliquer sur tout le mois affiché
            </Typography>
          </Box>

          <Box sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>

            {bulkDrawer.type === "price" && !bulkDrawer.ratePlanId && (
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.text, mb: 1 }}>
                  Choisir un tarif
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {drawerRoomPlans.map(plan => {
                    const u = unitOf(plan.stayType);
                    return (
                      <Box
                        key={plan._id}
                        onClick={() => setBulkDrawer(prev => ({ ...prev, ratePlanId: plan._id.toString() }))}
                        sx={{
                          px: 1.75, py: 1.25, borderRadius: 2,
                          border: `1px solid ${COLORS.border}`,
                          cursor: "pointer", fontSize: 13, fontWeight: 500,
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                          "&:hover": { borderColor: "var(--k-green)", backgroundColor: "var(--k-green-soft)" },
                        }}
                      >
                        <span>{plan.name}</span>
                        <Box component="span" sx={{ fontSize: 11, color: COLORS.muted, flexShrink: 0 }}>
                          / {u.label}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Le montant attendu n'est pas celui d'une nuit : c'est l'erreur
                la plus facile à commettre dans cette grille. */}
            {bulkDrawer.type === "price" && drawerIsPeriod && (
              <Box sx={{
                p: 2, borderRadius: 2,
                backgroundColor: "var(--k-green-soft)",
                border: "1px solid rgb(var(--k-green-rgb)/0.25)",
              }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "var(--k-green-hover)", mb: 0.5 }}>
                  Prix pour {drawerUnit.label === "mois" ? "un mois entier" : "une semaine entière"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#475569" }}>
                  Saisissez le montant total de {drawerUnit.nights} nuits, pas un prix par nuit.
                </Typography>
              </Box>
            )}

            <input
              type="number"
              placeholder={bulkDrawer.type === "inventory"
                ? "Nombre de chambres"
                : `Prix (€ / ${drawerUnit.label})`}
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              style={{
                padding: "11px 14px",
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                outline: "none",
                width: "100%",
              }}
            />

            {bulkValue > 0 && (
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#F8FAFC", border: `1px solid ${COLORS.border}` }}>
                <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 0.5 }}>Aperçu</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
                  {bulkDrawer.type === "price"
                    ? `${bulkValue} € / ${drawerUnit.label}`
                    : `${bulkValue} chambre${bulkValue > 1 ? "s" : ""}`}
                </Typography>
                {bulkDrawer.type === "price" && (
                  <>
                    <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.5 }}>
                      {formatKmf(eurToKmf(bulkValue))} KMF / {drawerUnit.label}
                    </Typography>
                    {drawerIsPeriod && (
                      <Typography sx={{ fontSize: 12, color: "var(--k-green)", fontWeight: 600, mt: 0.5 }}>
                        ≈ {Math.round(Number(bulkValue) / drawerUnit.nights)} € / nuit pour le client
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ p: 3, borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 1.5 }}>
            <Button
              fullWidth variant="outlined"
              onClick={() => setBulkDrawer({ open: false, type: null, roomId: null, ratePlanId: null })}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: COLORS.border, color: COLORS.text }}
            >
              Annuler
            </Button>
            <Button
              fullWidth variant="contained"
              disabled={!bulkValue || (bulkDrawer.type === "price" && !bulkDrawer.ratePlanId)}
              onClick={handleApplyBulk}
              sx={{
                borderRadius: 2, textTransform: "none", fontWeight: 700,
                backgroundColor: "var(--k-green)",
                "&:hover": { backgroundColor: "var(--k-green-hover)" },
              }}
            >
              Appliquer
            </Button>
          </Box>
        </Box>
      </Drawer>

      <BulkEditDrawer
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        rooms={rooms}
        onApply={handleBulkEditApply}
      />
    </Box>
  );
};

export default Calendar;